/**
 * Currency helper functions. Ported from Gravity Forms so we don't need to include entire scripts like
 * gform_admin_scripts, etc.
 */

/**
 * Currency configuration type
 */
export interface CurrencyConfig {
	name: string;
	symbol_left: string;
	symbol_right: string;
	symbol_padding: string;
	thousand_separator: string;
	decimal_separator: string;
	decimals: number;
	code: string;
}

const currency: CurrencyConfig = window.gp_bookings_form_editor_strings
	?.currency ||
	window.gp_bookings_admin_bookables_strings?.currency ||
	window.gp_bookings_field_booking_strings?.currency ||
	window.gp_bookings_field_booking_time_strings?.currency || {
		name: 'U.S. Dollar',
		symbol_left: '$',
		symbol_right: '',
		symbol_padding: '',
		thousand_separator: ',',
		decimal_separator: '.',
		decimals: 2,
		code: 'USD',
	};

/**
 * Get the current currency configuration
 */
export function getCurrency(): CurrencyConfig {
	return currency;
}

/**
 * Get the currency symbol (decoded from HTML entities)
 */
export function getCurrencySymbol(): string {
	const symbol = currency.symbol_left || currency.symbol_right;
	const code = currency.code;

	if (symbol) {
		return htmlDecode(symbol);
	}

	return code;
}

/**
 * Get the currency code if it exists
 */
export function getCurrencyCode(): string | false {
	return currency.code !== '' ? currency.code : false;
}

/**
 * Determines if the specified value is a number
 */
export function isNumeric(value: string | number): boolean {
	return !isNaN(parseFloat(value as string)) && isFinite(value as number);
}

/**
 * Decodes a string that has been HTML encoded
 */
export function htmlDecode(text: string): string {
	let d = text;
	const arr = d.match(/&#[0-9]{1,5};/g);

	if (arr !== null && arr !== undefined) {
		for (let x = 0; x < arr.length; x++) {
			const m = arr[x];
			const c = parseInt(m.substring(2, m.length - 1));
			if (c >= -32768 && c <= 65535) {
				d = d.replace(m, String.fromCharCode(c));
			} else {
				d = d.replace(m, '');
			}
		}
	}
	return d;
}

/**
 * Gets a formatted number and returns a clean "decimal dot" number
 * Uses the current currency configuration
 *
 * @param text The currency-formatted number
 * @return The unformatted numerical value or false if invalid
 */
export function cleanNumber(text: string | number): number | false {
	let cleanNum = '';
	let floatNumber = '';
	let isNegative = false;

	// Converting to a string if a number was passed
	text = text + ' ';

	// Removing symbol in unicode format (i.e. &#4444;)
	text = text.replace(/&.*?;/g, '');

	// Removing symbol from text
	text = text.replace(currency.symbol_right, '');
	text = text.replace(currency.symbol_left, '');

	// Removing all non-numeric characters
	for (let i = 0; i < text.length; i++) {
		const digit = text.substr(i, 1);
		if (
			(parseInt(digit, 10) >= 0 && parseInt(digit, 10) <= 9) ||
			digit === currency.decimal_separator
		) {
			cleanNum += digit;
		} else if (digit === '-') {
			isNegative = true;
		}
	}

	// Removing the thousand separators but keeping decimal point
	for (let i = 0; i < cleanNum.length; i++) {
		const digit = cleanNum.substr(i, 1);
		if (digit >= '0' && digit <= '9') {
			floatNumber += digit;
		} else if (digit === currency.decimal_separator) {
			floatNumber += '.';
		}
	}

	if (isNegative) {
		floatNumber = '-' + floatNumber;
	}

	return isNumeric(floatNumber) ? parseFloat(floatNumber) : false;
}

/**
 * Formats a number using the current currency configuration
 *
 * @param number Number to be formatted (must be clean, unformatted)
 * @param padded Pads output with zeroes if the number is exact
 * @return The formatted number
 */
export function numberFormat(
	number: string | number,
	padded: boolean = true
): string {
	const numStr = (number + '').replace(',', '').replace(' ', '');
	let n = !isFinite(+numStr) ? 0 : +numStr;
	const prec = !isFinite(+currency.decimals)
		? 0
		: Math.abs(currency.decimals);

	const toFixedFix = (num: number, precision: number): string => {
		const k = Math.pow(10, precision);
		return '' + Math.round(num * k) / k;
	};

	let s: string[];

	if (parseInt(currency.decimals as any) === 0) {
		n += 0.0000000001; // getting around floating point arithmetic issue when rounding
		s = ('' + Math.round(n)).split('.');
	} else if (parseInt(currency.decimals as any) === -1) {
		s = ('' + n).split('.');
	} else {
		n += 0.0000000001; // getting around floating point arithmetic issue when rounding
		s = toFixedFix(n, prec).split('.');
	}

	if (s[0].length > 3) {
		s[0] = s[0].replace(
			/\B(?=(?:\d{3})+(?!\d))/g,
			currency.thousand_separator
		);
	}

	if (padded) {
		if ((s[1] || '').length < prec) {
			s[1] = s[1] || '';
			s[1] += new Array(prec - s[1].length + 1).join('0');
		}
	}

	return s.join(currency.decimal_separator);
}

/**
 * Converts the specified text to a number
 *
 * @param text The text to be converted to a number
 * @return The cleaned number
 */
export function toNumber(text: string | number): number {
	if (isNumeric(text)) {
		return parseFloat(text as string);
	}
	const cleaned = cleanNumber(text);
	return cleaned === false ? 0 : cleaned;
}

/**
 * Attempts to clean the specified number and formats it as currency
 *
 * @param number Number to be formatted (can be clean or already formatted)
 * @param isNum  Whether the number is guaranteed to be clean/unformatted
 * @return A number formatted as currency
 */
export function toMoney(
	number: string | number,
	isNum: boolean = false
): string {
	let num = number;

	if (!isNum && typeof num !== 'number') {
		// Cleaning number, removing all formatting
		const cleaned = cleanNumber(num);
		if (cleaned === false) {
			return '';
		}
		num = cleaned;
	}

	const numStr = num + '';
	let negative = '';
	if (numStr[0] === '-') {
		num = parseFloat(numStr.substr(1));
		negative = '-';
	}

	let money = numberFormat(num);

	if (money === '0.00') {
		negative = '';
	}

	const symbolLeft = currency.symbol_left
		? currency.symbol_left + currency.symbol_padding
		: '';
	const symbolRight = currency.symbol_right
		? currency.symbol_padding + currency.symbol_right
		: '';

	money = negative + htmlDecode(symbolLeft) + money + htmlDecode(symbolRight);

	return money;
}

/**
 * Returns the decimal separator based on the specified number format
 *
 * @param format The number format to get the decimal separator for
 * @return The decimal separator
 */
export function getDecimalSeparator(
	format: 'currency' | 'decimal_comma' | 'decimal_dot'
): string {
	switch (format) {
		case 'currency':
			return currency.decimal_separator;
		case 'decimal_comma':
			return ',';
		default:
			return '.';
	}
}
